<template>
  <component
    :is="tag"
    v-on="$listeners"
    :disabled="disabled || isLoading"
    :class="['action', parentClass, customClass]"
    :href="href"
    :to="to"
  >
    <ActionLoader
      v-if="isLoading"
      :loaderClass="loaderClass"
      name="ball-clip-rotate"
    />
    <slot v-else name="icon">
      <span v-if="materialIcon" :class="['material-icons', iconClass]">{{
        materialIcon
      }}</span>
    </slot>
    {{ content }}
  </component>
</template>

<script>
import ActionLoader from '@/components/shared/ActionLoader.vue'

export default {
  name: 'Action',
  components: {
    ActionLoader,
  },
  props: {
    tag: {
      type: String,
      required: false,
      default: 'button',
    },
    ofType: {
      type: String,
      required: true,
      default: 'medium',
      validator: value =>
        [
          'large',
          'medium',
          'small',
          'trigger',
          'form-submit',
          'auth',
          'custom',
        ].indexOf(value) !== -1,
    },
    content: {
      type: String,
      required: true,
    },
    materialIcon: {
      type: String,
      required: false,
    },
    customClass: {
      type: String,
      required: false,
    },
    href: {
      type: String,
      required: false,
    },
    to: {
      type: Object,
      required: false,
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    parentClass() {
      if (this.ofType === 'custom') return ''
      if (this.ofType === 'large') return 'btn btn--primary'
      return `action__${this.ofType}`
    },
    iconClass() {
      if (this.ofType === 'custom' || this.ofType === 'large') return ''
      return `action__${this.ofType}--icon`
    },
    loaderClass() {
      if (this.ofType === 'custom' || this.ofType === 'large') return ''
      return `action__${this.ofType}--loader`
    },
  },
}
</script>

<style lang="scss" scoped>
.action {
  $this: &;
  &__large {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: rem(14px);
    font-family: var(--font-bold);
    color: var(--white);
    background: transparent;
    border: 3px solid var(--white);
    border-radius: 25px;
    text-transform: uppercase;
    text-decoration: none;
    cursor: pointer;
    white-space: nowrap;
    padding: 5px 0 5px 25px;
    min-width: 150px;
    max-width: 200px;
    &:hover {
      background: var(--white);
      color: var(--black);
      #{$this}__large--icon {
        color: var(--black);
        fill: var(--black);
      }
    }
    &--icon {
      color: var(--white);
      fill: var(--white);
      font-size: rem(27px);
      height: rem(27px);
      margin: 0 5px 0 25px;
    }
    &--loader {
      color: var(--white);
      margin: 0 5px 0 25px;
    }
  }
  &__medium {
    background: transparent;
    color: var(--white);
    text-transform: uppercase;
    border: 1px solid var(--gray);
    display: flex;
    align-items: center;
    padding: 5px 10px;
    min-width: 100px;
    font-size: rem(14px);
    border-radius: 25px;
    cursor: pointer;
    font-weight: 700;
    @include tablet {
      min-width: 130px;
      padding: 8px 20px;
    }
    &:hover {
      background: var(--orange);
      border-color: var(--black);
    }
    &--icon {
      color: var(--white);
      fill: var(--white);
      font-size: rem(22px);
      width: rem(16px);
      margin: 0 5px 0 auto;
    }
    &--loader {
      color: var(--white);
      margin: 0 5px 0 auto;
    }
  }
  &__small {
    background: transparent;
    color: var(--white);
    text-transform: uppercase;
    border: 1px solid var(--gray);
    display: flex;
    align-items: center;
    padding: 10px;
    font-size: rem(12px);
    border-radius: 25px;
    cursor: pointer;
    font-weight: 700;
    position: relative;
    &:hover {
      background: var(--orange);
      border-color: var(--black);
    }
    &--icon {
      display: none !important;
    }
    &--loader {
      display: none !important;
    }
  }
  &__trigger {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: flex-start;
    border: none;
    border-radius: 5px;
    background: var(--glass-mid-gray);
    height: 44px;
    width: 44px;
    padding: 10px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    &:hover {
      background: var(--white);
    }
    &--icon {
      font-size: rem(27px);
      height: rem(25px);
      width: rem(30px);
    }
    &--selected {
      border-bottom: 4px solid var(--green);
    }
    &--pill {
      background: var(--gray);
      border-radius: 50%;
      padding-top: 2px;
      width: 27px;
      height: 27px;
      position: absolute;
      top: -5px;
      right: -9px;
    }
    &--disabled {
      pointer-events: none;
    }
    &--loader {
      color: var(--orange);
    }
  }
  &__form-submit {
    background: transparent;
    border-radius: 25px;
    padding: 5px 10px;
    border: 2px solid var(--black);
    color: var(--black);
    text-transform: uppercase;
    display: flex;
    align-items: center;
    cursor: pointer;
    font-weight: 700;
    &:hover {
      color: var(--white);
      background: var(--black);
      border-color: var(--orange);
      .btn__form-submit--icon {
        color: var(--white);
        fill: var(--white);
      }
    }
    &:disabled,
    &[disabled] {
      pointer-events: none;
      opacity: 0.6;
    }
    &--icon {
      font-size: rem(24px);
      height: rem(24px);
      width: rem(24px);
      justify-self: flex-end;
      margin: 0 0 0 25px;
      color: var(--orange);
      fill: var(--orange);
      border-radius: 25px;
    }
  }
  &__auth {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 230px;
    white-space: nowrap;
    margin: 5px 0 5px 0;
    padding: 6px 7px;
    color: var(--white);
    font-size: 0.9rem;
    font-family: var(--font-bold);
    border-radius: 5px;
    cursor: pointer;
    &:hover {
      opacity: 0.8;
    }
    &--icon {
      margin: 0 0 0 auto;
      font-size: rem(24px);
      height: rem(24px);
      color: var(--orange);
    }
    &--loader {
      color: var(--white);
      margin: 0 0 0 auto;
    }
  }
}
</style>
